import { useState, useEffect } from 'react';
import { getNetworksList } from '@/services/api';
import type { NetworkListItem } from '@/types/network';
import { useDashboardStore } from '@/stores/useDashboardStore';

interface UseNetworksParams {
  type?: string;
  segment?: string;
  nodeId?: string;
  search?: string;
  tunnelState?: string;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export function useNetworks(params: UseNetworksParams = {}) {
  const refreshKey = useDashboardStore((state) => state.refreshKey);
  const [networks, setNetworks] = useState<NetworkListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const depsKey = JSON.stringify(params);

  useEffect(() => {
    let cancelled = false;
    async function fetch() {
      try {
        setLoading(true);
        const data = await getNetworksList(params);
        if (!cancelled) {
          setNetworks(data.networks);
          setTotal(data.pagination.total);
          setTotalPages(data.pagination.totalPages);
          setError(null);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err : new Error('Failed to fetch networks'));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }
    fetch();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [depsKey, refreshKey]);

  return { networks, total, totalPages, loading, error };
}
